import axios from "@/utils/axios";
import { toast } from "vue3-toastify";

const API_URL = process.env.VUE_APP_API_URL || "";

const commandeLinesCache = new Map();
const commandeLinesInflight = new Map();
const COMMAND_LINES_CACHE_TTL_MS = 5000;
const COMMAND_LINES_MIN_GAP_MS = 250;
let lastCommandeLinesRequest = 0;

export const invalidateCommandeLinesCache = (commandeId) => {
  if (!commandeId) return;
  commandeLinesCache.delete(commandeId);
};

export const getDevisData = async () => {
  const response = await axios.get(`${API_URL}/devis`);

  return response.data.data;
};

export const createCommand = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/commandes`, data);
    return response.data.data;
  } catch (error) {
    console.error(error);
    const message = error?.response?.data?.message || "Une erreur est survenue";
    toast.error(message);
    return null;
  }
};

export const updateCommand = async (id, data) => {
  const response = await axios.put(`${API_URL}/commandes/${id}`, data);

  return response.data.data;
};

export const getLignesCommandeData = async () => {
  const response = await axios.get(`${API_URL}/lignes-commande`);
  return response.data.data;
};

const fetchCommandeDetailsInternal = async (id, { force = false } = {}) => {
  if (!force) {
    const cached = commandeLinesCache.get(id);
    if (cached && Date.now() - cached.timestamp < COMMAND_LINES_CACHE_TTL_MS) {
      return cached.data;
    }

    if (commandeLinesInflight.has(id)) {
      return commandeLinesInflight.get(id);
    }
  }

  const request = (async () => {
    try {
      const elapsed = Date.now() - lastCommandeLinesRequest;
      if (elapsed < COMMAND_LINES_MIN_GAP_MS) {
        await new Promise((resolve) => setTimeout(resolve, COMMAND_LINES_MIN_GAP_MS - elapsed));
      }
      lastCommandeLinesRequest = Date.now();
      const response = await axios.get(`${API_URL}/commandes/${id}/details`);
      const commande = response?.data?.data ?? null;
      const lines = Array.isArray(commande?.lignes) ? commande.lignes : [];
      const payload = { commande, lines };
      commandeLinesCache.set(id, { data: payload, timestamp: Date.now() });
      return payload;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429) {
        toast.error("Trop de requêtes. Patientez quelques secondes avant de réessayer.");
      }
      throw error;
    } finally {
      commandeLinesInflight.delete(id);
    }
  })();

  commandeLinesInflight.set(id, request);
  return request;
};

export const getCommandeDetails = async (id, options = {}) => {
  const payload = await fetchCommandeDetailsInternal(id, options);
  return payload.commande;
};

export const getLignesCommandeDataByCommandeId = async (id, options = {}) => {
  const payload = await fetchCommandeDetailsInternal(id, options);
  return payload.lines;
};

export const createCommandLine = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/lignes-commande`, data);
    invalidateCommandeLinesCache(data.commande_id);
    return response.data.data;
  } catch (error) {
    console.error(error);
    const message = error?.response?.data?.message || "Une erreur est survenue";
    toast.error(message);
    return null;
  }
};

export const updateCommandLine = async (id, data) => {
  const response = await axios.put(`${API_URL}/lignes-commande/${id}`, data);
  invalidateCommandeLinesCache(data.commande_id);
  return response.data.data;
};

export const deleteCommandLine = async (id, commandeId = null) => {
  const response1 = await axios.delete(`${API_URL}/lignes-commande/${id}`);
  invalidateCommandeLinesCache(commandeId);
  return response1.data;
};

export const createDevis = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/devis`, data);
    return response.data.data;
  } catch (error) {
    console.error(error);
    const message = error?.response?.data?.message || "Une erreur est survenue";
    toast.error(message);
    return null;
  }
};

export const updateDevis = async (id, data) => {
  const response = await axios.put(`${API_URL}/devis/${id}`, data);
  return response.data.data;
};

export const deleteDevis = async (id_devis, id_commande) => {
  const response = await axios.delete(`${API_URL}/commandes/${id_commande}`);
  await axios.delete(`${API_URL}/devis/${id_devis}`);
  try {
    const data = await getLignesCommandeDataByCommandeId(id_commande);
    data.forEach(async (m) => {
      await deleteCommandLine(m.id, id_commande);
    });
  } catch (_) {
    /* ignore fetch/delete errors */
  }
  return response.data;
};
